import React, { memo, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  MenuItem,
  TextField,
  InputAdornment,
  Skeleton,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  FileCopy as FileCopyIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Sync as SyncIcon,
  DescriptionOutlined,
} from '@mui/icons-material';
import {
  GeneratedQuestionPaper,
  PaperStatus,
} from '../../services/generateQuestionPaperService';
import { Class, Subject, Center } from '../../types';
import PaperCard from './PaperCard';
import { elegantMenuProps } from '../../utils/menuStyles';
import FilterBar from '../common/FilterBar';
import SearchableSelect from '../common/SearchableSelect';
import EmptyState from '../common/EmptyState';

const STATUS_OPTIONS = Object.values(PaperStatus).map((s) => ({ value: s, label: s }));
const toOptions = (rows: Array<{ _id: string; name: string }>) => rows.map((r) => ({ value: r._id, label: r.name }));
const cardGrid = { xs: 12, sm: 6, md: 4, lg: 3 } as const;

interface PaperListViewProps {
  papers: GeneratedQuestionPaper[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  // Filters
  centers: Center[];
  classes: Class[];
  subjects: Subject[];
  selectedCenter: string;
  selectedClass: string;
  selectedSubject: string;
  selectedStatus: PaperStatus | '';
  searchQuery: string;
  isSystemAdmin: boolean | undefined;
  canEditModule: boolean;
  canViewModule: boolean;
  // Callbacks
  onCenterChange: (value: string) => void;
  onClassChange: (value: string) => void;
  onSubjectChange: (value: string) => void;
  onStatusChange: (value: PaperStatus | '') => void;
  onSearchChange: (value: string) => void;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onEdit: (paper: GeneratedQuestionPaper) => void;
  onView: (paper: GeneratedQuestionPaper) => void;
  onDelete: (paper: GeneratedQuestionPaper) => void;
  onDuplicate: (paper: GeneratedQuestionPaper) => void;
  onExportPDF: (paper: GeneratedQuestionPaper, includeAnswers: boolean) => void;
  onChangeStatus: (paper: GeneratedQuestionPaper, newStatus: PaperStatus) => void;
  /** Opens the confirm dialog + fires the bulk resync for this paper. The
   * parent owns the dialog + request state so we don't duplicate it. */
  onSyncFromBank: (paper: GeneratedQuestionPaper) => void;
  onCreateNew: () => void;
}


const PaperListView = memo<PaperListViewProps>(({
  papers,
  loading,
  page,
  rowsPerPage,
  totalCount,
  centers,
  classes,
  subjects,
  selectedCenter,
  selectedClass,
  selectedSubject,
  selectedStatus,
  searchQuery,
  isSystemAdmin,
  canEditModule,
  canViewModule,
  onCenterChange,
  onClassChange,
  onSubjectChange,
  onStatusChange,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  onEdit,
  onView,
  onDelete,
  onDuplicate,
  onExportPDF,
  onChangeStatus,
  onSyncFromBank,
  onCreateNew,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPaper, setSelectedPaper] = useState<GeneratedQuestionPaper | null>(null);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, paper: GeneratedQuestionPaper) => {
    setAnchorEl(event.currentTarget);
    setSelectedPaper(paper);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedPaper(null);
  }, []);

  const centerOptions = useMemo(() => toOptions(centers), [centers]);
  const classOptions = useMemo(() => toOptions(classes), [classes]);
  const subjectOptions = useMemo(() => toOptions(subjects), [subjects]);
  const filtered = !!(selectedClass || selectedSubject || selectedStatus || searchQuery);

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Paper Builder
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage comprehensive question papers from your question bank
          </Typography>
        </Box>
        {canEditModule && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateNew}
            sx={{ borderRadius: 2 }}
          >
            Create Question Paper
          </Button>
        )}
      </Box>

      {/* Filters */}
      <FilterBar>
        {isSystemAdmin && (
          <SearchableSelect label="Center" placeholder="All centers" value={selectedCenter} onChange={onCenterChange} options={centerOptions} minWidth={180} />
        )}
        <SearchableSelect label="Class" placeholder="All classes" value={selectedClass} onChange={onClassChange} options={classOptions} minWidth={160} />
        <SearchableSelect
          label="Subject"
          placeholder={selectedClass ? 'All subjects' : 'Pick a class first'}
          value={selectedSubject}
          onChange={onSubjectChange}
          options={subjectOptions}
          disabled={!selectedClass}
          minWidth={180}
        />
        <SearchableSelect
          label="Status"
          placeholder="Any status"
          value={selectedStatus}
          onChange={(v) => onStatusChange(v as PaperStatus | '')}
          options={STATUS_OPTIONS}
          minWidth={150}
        />
        <TextField
          size="small"
          placeholder="Search by title or description"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 220 }}
        />
      </FilterBar>

      {/* Papers */}
      {loading ? (
        <Grid container spacing={2}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Grid size={cardGrid} key={i}>
              <Skeleton variant="rounded" height={196} sx={{ borderRadius: '14px' }} />
            </Grid>
          ))}
        </Grid>
      ) : papers.length === 0 ? (
        <EmptyState
          icon={<DescriptionOutlined />}
          title={filtered ? 'No papers match' : 'No question papers yet'}
          description={filtered
            ? 'Try a different class, subject, status or search.'
            : 'Build a paper from your question bank: pick the questions, set the marks, and print it.'}
          action={!filtered && canEditModule ? { label: 'Create question paper', icon: <AddIcon />, onClick: onCreateNew } : undefined}
          sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}
        />
      ) : (
        <>
          <Grid container spacing={2}>
            {papers.map((paper) => (
              <Grid size={cardGrid} key={paper._id}>
                <PaperCard
                  paper={paper}
                  canEditModule={canEditModule}
                  canViewModule={canViewModule}
                  onEdit={onEdit}
                  onView={onView}
                  onMenuOpen={handleMenuOpen}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {!loading && papers.length > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => onPageChange(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                onRowsPerPageChange(parseInt(event.target.value, 10));
              }}
              rowsPerPageOptions={[12, 24, 36, 48]}
              labelRowsPerPage="Papers per page:"
              sx={{ mt: 2, width: "100%", overflow: "visible" }}
            />
          )}
        </>
      )}

      {/* Actions Menu — shared elegant style (compact, tight padding, dense
          list, soft shadow). See `utils/menuStyles.ts`. */}
      <Menu
        {...elegantMenuProps}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* View — for any non-DRAFT paper (published OR archived) */}
        {selectedPaper && selectedPaper.status !== PaperStatus.DRAFT && canViewModule && (
          <MenuItem onClick={() => {
            if (selectedPaper) onView(selectedPaper);
            handleMenuClose();
          }}>
            <ListItemIcon><VisibilityIcon /></ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        )}
        {/* Edit — DRAFT only */}
        {selectedPaper?.status === PaperStatus.DRAFT && canEditModule && (
          <MenuItem onClick={() => {
            if (selectedPaper) onEdit(selectedPaper);
            handleMenuClose();
          }}>
            <ListItemIcon><EditIcon /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canEditModule && (
          <MenuItem onClick={() => {
            if (selectedPaper) onDuplicate(selectedPaper);
            handleMenuClose();
          }}>
            <ListItemIcon><FileCopyIcon /></ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>
        )}
        {/* Sync every bank-linked row on this paper with the latest version
            from the question bank. Opens a confirm dialog in the parent so
            the author knows local edits will be overwritten. */}
        {canEditModule && (
          <MenuItem onClick={() => {
            if (selectedPaper) onSyncFromBank(selectedPaper);
            handleMenuClose();
          }}>
            <ListItemIcon><SyncIcon /></ListItemIcon>
            <ListItemText>Sync from Bank</ListItemText>
          </MenuItem>
        )}
        {canViewModule && (
          <>
            <MenuItem onClick={() => {
              if (selectedPaper) onExportPDF(selectedPaper, false);
              handleMenuClose();
            }}>
              <ListItemIcon><DownloadIcon /></ListItemIcon>
              <ListItemText>Export PDF</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              if (selectedPaper) onExportPDF(selectedPaper, true);
              handleMenuClose();
            }}>
              <ListItemIcon><DownloadIcon /></ListItemIcon>
              <ListItemText>Export PDF (with answers)</ListItemText>
            </MenuItem>
          </>
        )}
        {/* Archive (only for PUBLISHED). Archived papers stay readable but
            no longer block question deletes. */}
        {selectedPaper?.status === PaperStatus.PUBLISHED && canEditModule && (
          <>
            <Divider />
            <MenuItem onClick={() => {
              if (selectedPaper) onChangeStatus(selectedPaper, PaperStatus.ARCHIVED);
              handleMenuClose();
            }}>
              <ListItemIcon><ArchiveIcon /></ListItemIcon>
              <ListItemText>Archive</ListItemText>
            </MenuItem>
          </>
        )}
        {/* Unarchive (only for ARCHIVED) — restore back to PUBLISHED */}
        {selectedPaper?.status === PaperStatus.ARCHIVED && canEditModule && (
          <>
            <Divider />
            <MenuItem onClick={() => {
              if (selectedPaper) onChangeStatus(selectedPaper, PaperStatus.PUBLISHED);
              handleMenuClose();
            }}>
              <ListItemIcon><UnarchiveIcon /></ListItemIcon>
              <ListItemText>Unarchive</ListItemText>
            </MenuItem>
          </>
        )}
        {canEditModule && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                if (selectedPaper) onDelete(selectedPaper);
                handleMenuClose();
              }}
              sx={{ color: 'error.main', '& .MuiListItemIcon-root': { color: 'error.main' } }}
            >
              <ListItemIcon><DeleteIcon /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
});

PaperListView.displayName = 'PaperListView';

export default PaperListView;
